import { useTemplateBookmarkContext } from "../contexts/TemplateBookmarkContext";

/**
 * Hook for simplified template bookmark actions.
 *
 * Provides a clean API for managing bookmarks for a specific template.
 * Handles missing context gracefully by returning safe defaults.
 *
 * @param templateId - The ID of the template to manage bookmarks for
 * @returns Bookmark state and actions for the specified template
 *
 * @example
 * ```tsx
 * const bookmark = useTemplateBookmarkActions(template.id);
 *
 * return (
 *   <BookmarkButton
 *     isBookmarked={bookmark.isBookmarked}
 *     onSave={bookmark.onSave}
 *     onUnsave={bookmark.onUnsave}
 *     isLoading={bookmark.isLoading}
 *   />
 * );
 * ```
 */
export type UseTemplateBookmarkActionsReturn = {
  /** Whether the template is currently bookmarked */
  isBookmarked: boolean;
  /** Save/bookmark the template */
  onSave: (id: string) => Promise<void>;
  /** Remove bookmark from the template */
  onUnsave: (id: string) => Promise<void>;
  /** Whether a bookmark operation is in progress for this template */
  isLoading: boolean;
};

export function useTemplateBookmarkActions(
  templateId: string | undefined,
): UseTemplateBookmarkActionsReturn {
  const context = useTemplateBookmarkContext();

  const onSave = async (id: string) => {
    const targetId = id || templateId;
    if (!targetId || !context?.createUserTemplateBookmark) return;
    await context.createUserTemplateBookmark(targetId);
  };

  const onUnsave = async (id: string) => {
    const targetId = id || templateId;
    if (!targetId || !context?.deleteUserTemplateBookmark) return;
    await context.deleteUserTemplateBookmark(targetId);
  };

  return {
    isBookmarked: !!(templateId && context?.isBookmarked?.(templateId)),
    onSave,
    onUnsave,
    isLoading: !!(
      templateId && context?.templateBookmarkChanging === templateId
    ),
  };
}
